<template>
  <div ref="inviteRoot" class="invite-dialog-container" :class="{ 'is-mobile': mobile }" @keydown.esc="handleEscape">
    
    <div v-show="mobile || currentView === 'main'" class="invite-main view-panel" :class="{ 'is-domestic': isDomestic }" :inert="mobile && currentView === 'history'" :aria-hidden="mobile && currentView === 'history' ? 'true' : null">
      <MobileInviteHeader v-if="mobile" ref="mobileHeader" :title="$t('layout.layout.refer_friend')" @back="$emit('close')" />
      <!-- 统一的关闭按钮位置 -->
      <button v-if="!mobile" type="button" class="invite-close" :aria-label="$t('common.close')" @click="$emit('close')">
        <el-icon :size="20"><Close /></el-icon>
      </button>

      <div class="invite-header-icon">
        <img src="https://osspub.paiteams.com/pub-paiteams/fData/2026/0416/69e080dcc884f.jpeg" alt="invite" class="invite-header-img" />
      </div>

      <div class="invite-text">
        <h2 class="invite-title">{{ $t('views.invite.invite_title') }}</h2>
        <!-- 1. 积分副标题骨架 -->
        <div class="invite-subtitle">
          <el-skeleton v-if="isLoading" animated style="width: 220px; display: flex; align-items: center;">
            <template #template>
              <el-skeleton-item variant="text" style="height: 16px; width: 100%;" />
            </template>
          </el-skeleton>
          <span v-else v-html="subtitleHtml"></span>
        </div>
      </div>

      <div class="invite-actions">
        <div class="invite-code-display">
          <span class="code-label">{{ $t('views.invite.invite_code_label') }}</span>
          <div class="code-value-row">
            <!-- 2. 邀请码数字骨架 -->
            <el-skeleton v-if="isLoading" animated style="width: 90px; display: flex; align-items: center;">
              <template #template>
                <el-skeleton-item variant="text" style="height: 18px; width: 100%;" />
              </template>
            </el-skeleton>
            <span v-else class="code-value">{{ inviteData.invite_code || '-' }}</span>
            
            <button type="button" class="code-copy-btn" :disabled="isLoading || !inviteData.invite_code" :aria-label="$t('common.copy')" @click="copyCode">
              <el-icon><DocumentCopy /></el-icon>
            </button>
          </div>
        </div>
        
        <div class="action-buttons-row">
          <el-button class="btn-copy-link" :disabled="isLoading || !inviteData.invite_code" @click="copyLink">
            <el-icon><Link /></el-icon>
            {{ $t('views.invite.copy_link') }}
          </el-button>
          <el-button v-if="!isDomestic" class="btn-invite-facebook" type="primary" :disabled="isLoading || !inviteData.invite_code" @click="shareFacebook">
            <svg-icon icon-class="facebook" class-name="fb-icon" />
            {{ $t('views.invite.invite_facebook') }}
          </el-button>
        </div>
      </div>

      <div v-if="!isDomestic" class="invite-social">
        <button type="button" class="social-btn" :disabled="isLoading || !inviteData.invite_code" title="WhatsApp" aria-label="WhatsApp" @click="shareWhatsApp">
          <svg-icon icon-class="whatsapp" class-name="social-icon" />
        </button>
        <button type="button" class="social-btn" :disabled="isLoading || !inviteData.invite_code" title="X" aria-label="X" @click="shareX">
          <svg-icon icon-class="x-twitter" class-name="social-icon" />
        </button>
        <button type="button" class="social-btn" :disabled="isLoading || !inviteData.invite_code" title="LinkedIn" aria-label="LinkedIn" @click="shareLinkedIn">
          <svg-icon icon-class="linkedin" class-name="social-icon" />
        </button>
        <button type="button" class="social-btn" :disabled="isLoading || !inviteData.invite_code" title="Reddit" aria-label="Reddit" @click="shareReddit">
          <svg-icon icon-class="reddit" class-name="social-icon" />
        </button>
      </div>

      <div ref="historyTrigger" class="invite-stats-card" role="button" tabindex="0" @click="goToHistory" @keydown.enter.prevent="goToHistory" @keydown.space.prevent="goToHistory">
        <div class="stats-left">
          <div class="stats-label">{{ $t('views.invite.invitations') }}</div>
          <div class="stats-numbers">
            <span class="stats-credit">
              <!-- 统计数字也加上骨架，防止高度跳动 -->
              <el-skeleton v-if="isLoading" animated style="width: 40px; display: flex; align-items: center;">
                <template #template><el-skeleton-item variant="text" style="height: 20px;" /></template>
              </el-skeleton>
              <span v-else class="stats-value">{{ formatNumber(statsData.total_reward_points || 0) }}</span>
              <span class="stats-unit">{{ $t('views.invite.credits') }}</span>
            </span>
            <span class="stats-divider">/</span>
            <span class="stats-referral">
              <el-skeleton v-if="isLoading" animated style="width: 20px; display: flex; align-items: center;">
                <template #template><el-skeleton-item variant="text" style="height: 20px;" /></template>
              </el-skeleton>
              <span v-else class="stats-value">{{ formatNumber(statsData.total_invited || 0) }}</span>
              <span class="stats-unit">{{ $t('views.invite.referrals') }}</span>
            </span>
          </div>
        </div>
        <div class="stats-right">
          <svg-icon icon-class="users-outline" class-name="users-icon" />
          <el-icon class="arrow-icon"><ArrowRight /></el-icon>
        </div>
      </div>
    </div>

    <component
      :is="mobile ? 'MobileInviteHistorySheet' : 'div'"
      ref="mobileHistory"
      v-show="currentView === 'history'"
      class="invite-history view-panel"
      :title="mobile ? $t('views.invite.history_title') : null"
      @close="closeHistory"
    >
      <!-- 3. 调整历史界面的 Header 布局，使其按钮位置与主界面完全一致 -->
      <div v-if="!mobile" class="history-header">
        <button ref="historyBack" type="button" class="history-back" :aria-label="$t('layout.layout.back')" @click="closeHistory">
          <el-icon :size="20"><ArrowLeft /></el-icon>
        </button>
        <div class="history-title">{{ $t('views.invite.history_title') }}</div>
        <button type="button" class="history-close" :aria-label="$t('common.close')" @click="$emit('close')">
          <el-icon :size="20"><Close /></el-icon>
        </button>
      </div>

      <div class="history-body">
        <!-- 首次加载骨架屏 -->
        <div v-if="recordsLoading && recordsList.length === 0" class="records-scroll-wrapper">
          <div class="records-list">
            <div v-for="i in 6" :key="'skeleton-' + i" class="record-item record-skeleton">
              <div class="record-avatar">
                <el-skeleton animated>
                  <template #template>
                    <el-skeleton-item variant="circle" style="width: 40px; height: 40px;" />
                  </template>
                </el-skeleton>
              </div>
              <div class="record-info">
                <el-skeleton animated style="width: 60%;">
                  <template #template>
                    <el-skeleton-item variant="text" style="height: 14px;" />
                  </template>
                </el-skeleton>
                <el-skeleton animated style="width: 80%; margin-top: 8px;">
                  <template #template>
                    <el-skeleton-item variant="text" style="height: 12px;" />
                  </template>
                </el-skeleton>
              </div>
              <div class="record-reward">
                <el-skeleton animated style="width: 40px;">
                  <template #template>
                    <el-skeleton-item variant="text" style="height: 15px;" />
                  </template>
                </el-skeleton>
                <el-skeleton animated style="width: 40px; margin-top: 4px;">
                  <template #template>
                    <el-skeleton-item variant="text" style="height: 11px;" />
                  </template>
                </el-skeleton>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!recordsLoading && recordsError && recordsList.length === 0" class="history-error">
          <p>{{ recordsError }}</p>
          <button type="button" class="history-retry" @click="retryRecords">{{ $t('views.invite.history_retry') }}</button>
        </div>

        <div v-if="!recordsLoading && !recordsError && recordsList.length === 0" class="history-empty">
          <svg-icon icon-class="empty-invite" class-name="empty-icon" />
          <p>{{ $t('views.invite.no_records') }}</p>
        </div>

        <div 
          v-if="recordsList.length > 0"
          class="records-scroll-wrapper"
          v-infinite-scroll="loadMoreRecords"
          :infinite-scroll-disabled="currentView !== 'history' || recordsLoading || noMore || !!recordsError"
          :infinite-scroll-distance="20"
        >
          <div class="records-list">
            <div
              v-for="record in recordsList"
              :key="record.id"
              class="record-item"
            >
              <div class="record-avatar">
                <el-avatar :size="40" :src="record.invited_user_avatar">
                  <el-icon :size="20"><User /></el-icon>
                </el-avatar>
              </div>
              <div class="record-info">
                <div class="record-name">{{ record.invited_user_name || $t('views.invite.anonymous_user') }}</div>
                <div class="record-meta">
                  <span class="record-time">{{ record.create_time }}</span>
                  <span class="record-status" :class="`status-${record.inviter_reward_status}`">
                    {{ $t(getRewardStatusText(record.inviter_reward_status)) }}
                  </span>
                </div>
              </div>
              <div class="record-reward">
                <span class="reward-points">+{{ formatNumber(record.inviter_reward_points) }}</span>
                <span class="reward-label">{{ $t('views.invite.credits') }}</span>
              </div>
            </div>
          </div>
          
          <div v-if="recordsLoading" class="scroll-loading" role="status">
            <el-icon class="is-loading"><Loading /></el-icon> {{ $t('common.loading') }}...
          </div>
          <div v-if="!recordsLoading && recordsError" class="history-inline-error">
            <span>{{ recordsError }}</span>
            <button type="button" class="history-retry" @click="retryRecords">{{ $t('views.invite.history_retry') }}</button>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { Close, ArrowRight, ArrowLeft, Link, User, DocumentCopy, Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import SvgIcon from '@/components/SvgIcon/index.vue'
import MobileInviteHeader from '@/views/mobile/layout/components/MobileInviteHeader.vue'
import MobileInviteHistorySheet from '@/views/mobile/layout/components/MobileInviteHistorySheet.vue'
import { getInviteCode, getInviteRecords } from '@/api/invite'
import { writeTextToClipboard } from '@/utils/clipboard'

export default {
  name: 'InviteDialog',
  components: { Close, ArrowRight, ArrowLeft, Link, User, DocumentCopy, Loading, SvgIcon, MobileInviteHeader, MobileInviteHistorySheet },
  props: { mobile: { type: Boolean, default: false } },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const currentView = ref('main')
    const inviteRoot = ref(null)
    const mobileHeader = ref(null)
    const historyTrigger = ref(null)
    const historyBack = ref(null)
    const mobileHistory = ref(null)
    const inviteData = ref({})
    const statsData = ref({})
    const isLoading = ref(true) // 新增：控制骨架屏状态
    const isDomestic = process.env.VUE_APP_LEGAL_REGION === 'domestic'

    const formatNumber = (num) => {
      if (!num && num !== 0) return '0'
      return num.toLocaleString()
    }

    const subtitleHtml = computed(() => {
      const points = formatNumber(statsData.value.inviter_reward_points || 0)
      return t('views.invite.invite_subtitle', { points })
    })
    
    const recordsList = ref([])
    const recordsLoading = ref(false)
    const recordsError = ref('')
    let recordsRequestId = 0
    let historySession = 0
    let disposed = false
    const pagination = ref({
      page: 1,
      page_size: 6,
      total: 0
    })
    
    const noMore = computed(() => {
      return recordsList.value.length >= pagination.value.total
    })

    const getRewardStatusText = (status) => {
      const map = {
        0: 'views.invite.status_pending',
        1: 'views.invite.status_issued',
        2: 'views.invite.status_failed',
        3: 'views.invite.status_limit',
        4: 'views.invite.status_error'
      }
      return map[status] || status
    }

    const loadInviteData = async () => {
      isLoading.value = true // 开始加载
      try {
        const codeRes = await getInviteCode()
        if (disposed) return
        if (codeRes.code === 0) {
          inviteData.value = codeRes.data
          statsData.value = {
            total_reward_points: codeRes.data.total_reward_points || 0,
            total_invited: codeRes.data.invite_count || 0,
            inviter_reward_points: codeRes.data.inviter_reward_points || 0
          }
        }
      } catch (e) {
        console.error('loadInviteData error', e)
      } finally {
        if (!disposed) isLoading.value = false // 结束加载
      }
    }

    const loadRecords = async (isReset = false) => {
      if (disposed || currentView.value !== 'history' || recordsLoading.value) return
      const session = historySession
      const requestId = ++recordsRequestId
      const targetPage = isReset ? 1 : pagination.value.page + 1
      if (isReset) {
        pagination.value.page = 1
        pagination.value.total = 0
        recordsList.value = []
      }
      recordsError.value = ''
      recordsLoading.value = true
      try {
        const res = await getInviteRecords({
          page: targetPage,
          page_size: pagination.value.page_size
        })
        if (disposed || requestId !== recordsRequestId || session !== historySession || currentView.value !== 'history') return
        const records = res?.data?.data || []
        if (res?.code !== 0 || !res.data || !Array.isArray(records)) {
          throw new Error(res?.message || t('views.invite.history_load_failed'))
        }
        if (isReset) recordsList.value = records
        else recordsList.value.push(...records)
        // 仅在本页成功后提交页码，失败重试仍请求同一页。
        pagination.value.page = targetPage
        pagination.value.total = res.data.total || 0
      } catch (e) {
        if (!disposed && requestId === recordsRequestId && session === historySession && currentView.value === 'history') {
          console.error('loadRecords error', e)
          recordsError.value = t('views.invite.history_load_failed')
        }
      } finally {
        if (!disposed && requestId === recordsRequestId && session === historySession) recordsLoading.value = false
      }
    }

    const loadMoreRecords = () => {
      if (noMore.value || recordsError.value) return
      return loadRecords()
    }

    const retryRecords = () => {
      return loadRecords(recordsList.value.length === 0)
    }

    const getShareUrl = () => {
      return `${window.location.origin}/invitation/${inviteData.value.invite_code}`
    }

    const getShareText = () => {
      return t('views.invite.share_text')
    }

    const copyLink = async () => {
      if (isLoading.value || !inviteData.value.invite_code) return
      const text = `${getShareText()}\n${getShareUrl()}`
      const copied = await writeTextToClipboard(text)
      ElMessage[copied ? 'success' : 'error'](t(copied ? 'common.copy_success' : 'common.copy_failed'))
    }

    const copyCode = async () => {
      const code = inviteData.value.invite_code
      if (isLoading.value || !code) return
      const copied = await writeTextToClipboard(code)
      ElMessage[copied ? 'success' : 'error'](t(copied ? 'common.copy_success' : 'common.copy_failed'))
    }

    const shareTo = (url) => {
      if (isLoading.value || !inviteData.value.invite_code) return
      window.open(url, '_blank', 'width=600,height=500')
    }
    const shareWhatsApp = () => shareTo(`https://wa.me/?text=${encodeURIComponent(`${getShareText()}\n${getShareUrl()}`)}`)
    const shareX = () => shareTo(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${getShareText()}\n${getShareUrl()}`)}`)
    const shareLinkedIn = () => shareTo(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`)
    const shareReddit = () => shareTo(`https://reddit.com/submit?url=${encodeURIComponent(getShareUrl())}&title=${encodeURIComponent(getShareText())}`)
    const shareFacebook = () => shareTo(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}&quote=${encodeURIComponent(getShareText())}`)

    const isVisibleFocusTarget = element => {
      if (!element?.isConnected || !element.getClientRects().length || element.closest('[inert], [aria-hidden="true"]')) return false
      const style = window.getComputedStyle(element)
      return style.display !== 'none' && style.visibility !== 'hidden'
    }

    const focusHistory = () => {
      if (props.mobile) mobileHistory.value?.focusClose()
      else if (isVisibleFocusTarget(historyBack.value)) historyBack.value.focus({ preventScroll: true })
    }

    const goToHistory = () => {
      if (disposed || currentView.value === 'history') return
      const session = ++historySession
      currentView.value = 'history'
      loadRecords(true)
      nextTick(() => {
        if (!disposed && session === historySession && currentView.value === 'history') focusHistory()
      })
    }

    const closeHistory = () => {
      if (currentView.value !== 'history') return
      const session = ++historySession
      // 本地失效不取消 HTTP；迟回包和旧 finally 均不能接管下一次打开。
      recordsRequestId += 1
      recordsLoading.value = false
      currentView.value = 'main'
      nextTick(() => {
        if (!disposed && session === historySession && currentView.value === 'main' && isVisibleFocusTarget(historyTrigger.value)) {
          historyTrigger.value.focus({ preventScroll: true })
        }
      })
    }

    const handleEscape = event => {
      if (!props.mobile) return
      event.preventDefault()
      event.stopPropagation()
      if (currentView.value === 'history') closeHistory()
      else emit('close')
    }

    onMounted(() => {
      loadInviteData()
    })

    watch(() => props.mobile, () => {
      const session = historySession
      nextTick(() => {
        if (disposed || session !== historySession || !isVisibleFocusTarget(inviteRoot.value)) return
        const active = document.activeElement
        if (isVisibleFocusTarget(active) && active !== document.body && !inviteRoot.value.contains(active)) return
        if (currentView.value === 'history') focusHistory()
        else if (!isVisibleFocusTarget(active) || active === document.body) {
          if (props.mobile) mobileHeader.value?.focusBack()
          else if (isVisibleFocusTarget(historyTrigger.value)) historyTrigger.value.focus({ preventScroll: true })
        }
      })
    }, { flush: 'post' })

    onBeforeUnmount(() => {
      disposed = true
      recordsRequestId += 1
      historySession += 1
    })

    return {
      currentView,
      inviteRoot,
      mobileHeader,
      historyTrigger,
      historyBack,
      mobileHistory,
      inviteData,
      statsData,
      isLoading, // 导出 isLoading
      isDomestic,
      recordsList,
      recordsLoading,
      recordsError,
      pagination,
      noMore,
      subtitleHtml,
      formatNumber,
      getRewardStatusText,
      copyLink,
      copyCode,
      shareWhatsApp,
      shareX,
      shareLinkedIn,
      shareReddit,
      shareFacebook,
      goToHistory,
      closeHistory,
      handleEscape,
      loadMoreRecords,
      retryRecords
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/mobile/layout/styles/invite-recommend.scss';

.invite-dialog-container {
  width: 100%;
  height: 580px;
  position: relative;
  overflow: hidden;
  background: var(--agst-bg-color, #fff);
}

.view-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.invite-main {
  padding: 24px;
  text-align: center;
}

/* 统一主界面的关闭按钮位置 */
.invite-close {
  padding: 0;
  border: 0;
  background: transparent;
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--agst-text-color-secondary, #909399);
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;
  &:hover { 
    color: var(--agst-text-color-primary, #303133); 
    background: var(--agst-fill-color-light, #f5f7fa);
  }
}

.invite-header-icon {
  position: relative;
  width: 100%;
  height: 80px;
  min-height: 120px;
  max-height: 120px;
  margin-bottom: 16px;
  display: block;
}

.invite-header-img {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.invite-text {
  margin-bottom: 24px;

  .invite-title {
    font-size: 22px;
    font-weight: 700;
    color: var(--agst-text-color-primary, #303133);
    margin: 0 0 8px;
    line-height: 1.3;
  }

  /* 增加 min-height 和 flex 居中，保证骨架加载前后高度0抖动 */
  .invite-subtitle {
    font-size: 14px;
    color: var(--agst-text-color-secondary, #909399);
    margin: 0;
    line-height: 1.5;
    min-height: 21px; 
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(em) {
      font-style: normal;
      color: var(--agst-color-primary, #2D66F0);
      font-weight: 600;
    }
  }
}

.invite-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;

  .invite-code-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background-color: var(--agst-bg-color-page, #f2f3f5);
    border-radius: 10px;
    --el-skeleton-color: var(--agst-fill-color, #e4e7ed);
    --el-skeleton-to-color: var(--agst-border-color, #dcdfe6);

    .code-label {
      font-size: 13px;
      color: var(--agst-text-color-secondary, #909399);
    }

    /* 增加 min-height 保证骨架高度不塌陷 */
    .code-value-row {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 28px; 
    }

    .code-value {
      font-size: 14px;
      font-weight: 600;
      color: var(--agst-text-color-primary, #303133);
      font-family: 'SF Mono', 'Consolas', monospace;
    }

    .code-copy-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      border: 0;
      border-radius: 6px;
      background: transparent;
      cursor: pointer;
      color: var(--agst-text-color-secondary, #909399);
      transition: all 0.2s;

      &:hover {
        color: var(--agst-color-primary, #2D66F0);
        background-color: var(--agst-fill-color, #e6e8eb);
      }
    }
  }

  .action-buttons-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    
    .el-button {
      margin: 0;
      width: 100%;
      height: 48px;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .btn-copy-link {
      background-color: #303133;
      border-color: #303133;
      color: #fff;
      &:hover { background-color: #1a1a1a; }
    }

    .btn-invite-facebook {
      background-color: var(--agst-color-primary, #2D66F0);
      border-color: var(--agst-color-primary, #2D66F0);
      .fb-icon { font-size: 18px; }
    }
  }
}

.invite-social {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin-bottom: auto;

  .social-btn {
    flex: 1;
    height: 44px;
    padding: 0;
    border-radius: 12px;
    background-color: var(--agst-bg-color-page, #f2f3f5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid transparent;

    &:hover {
      border-color: var(--agst-color-primary, #2D66F0);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }

    .social-icon {
      font-size: 20px;
    }
  }
}

.invite-stats-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--agst-bg-color-page, #f2f3f5);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;
  --el-skeleton-color: var(--agst-fill-color, #e4e7ed);
  --el-skeleton-to-color: var(--agst-border-color, #dcdfe6);

  &:hover {
    background-color: var(--agst-fill-color, #e6e8eb);
  }

  .stats-left {
    text-align: left;

    .stats-label {
      font-size: 13px;
      color: var(--agst-text-color-secondary, #909399);
      margin-bottom: 6px;
    }

    .stats-numbers {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 24px; /* 防止骨架跳动 */
    }

    .stats-credit,
    .stats-referral {
      display: inline-flex;
      align-items: baseline;
      gap: 4px;
    }

    .stats-value {
      font-size: 18px;
      font-weight: 700;
      color: var(--agst-text-color-primary, #303133);
    }

    .stats-unit {
      font-size: 12px;
      color: var(--agst-text-color-secondary, #909399);
    }

    .stats-divider {
      color: var(--agst-text-color-secondary, #909399);
    }
  }

  .stats-right {
    display: flex;
    align-items: center;
    gap: 4px;

    .users-icon {
      font-size: 24px;
      color: var(--agst-text-color-secondary, #909399);
    }

    .arrow-icon {
      color: var(--agst-text-color-secondary, #909399);
    }
  }
}

/* 国内版隐藏海外分享入口后，统计卡片仍固定在弹窗底部 */
.invite-main.is-domestic .invite-stats-card {
  margin-top: auto;
}

.invite-history {
  padding: 0;
}

/* 统一历史界面的 Header，使用绝对定位对齐按钮 */
.history-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px; /* 固定高度 */
  padding: 0;
  border-bottom: 1px solid var(--agst-border-color, #ebeef5);
  flex-shrink: 0;

  .history-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--agst-text-color-primary, #303133);
  }

  /* 返回按钮绝对定位，与主界面关闭按钮水平对称 */
  .history-back {
    position: absolute;
    left: 14px;
    top: 14px;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    color: var(--agst-text-color-secondary, #909399);
    transition: all 0.2s;
    &:hover { 
      color: var(--agst-text-color-primary, #303133); 
      background: var(--agst-fill-color-light, #f5f7fa);
    }
  }
  
  /* 关闭按钮绝对定位，与主界面完全一致 */
  .history-close {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: var(--agst-text-color-secondary, #909399);
    transition: all 0.2s;
    &:hover { 
      color: var(--agst-text-color-primary, #303133); 
      background: var(--agst-fill-color-light, #f5f7fa);
    }
  }
}

.history-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.history-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--agst-text-color-secondary, #909399);

  .empty-icon {
    font-size: 64px;
    opacity: 0.3;
    margin-bottom: 16px;
  }
}

.records-scroll-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--agst-border-color, #dcdfe6);
    border-radius: 3px;
  }
}

.records-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.record-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: var(--agst-bg-color-page, #f2f3f5);
  border-radius: 10px;

  &.record-skeleton {
    .record-info {
      display: flex;
      flex-direction: column;
    }

    .record-reward {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
  }

  .record-avatar {
    flex-shrink: 0;
  }

  .record-info {
    flex: 1;
    min-width: 0;

    .record-name {
      font-size: 14px;
      font-weight: 600;
      color: var(--agst-text-color-primary, #303133);
      margin-bottom: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .record-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: var(--agst-text-color-secondary, #909399);

      .record-status {
        &.status-0 { color: #f59e0b; }
        &.status-1 { color: #21ba45; }
        &.status-2 { color: #E71E1E; }
        &.status-3 { color: #f59e0b; }
        &.status-4 { color: #E71E1E; }
      }
    }
  }

  .record-reward {
    text-align: right;
    flex-shrink: 0;

    .reward-points {
      display: block;
      font-size: 15px;
      font-weight: 700;
      color: var(--agst-color-primary, #2D66F0);
    }

    .reward-label {
      font-size: 11px;
      color: var(--agst-text-color-secondary, #909399);
    }
  }
}

.scroll-loading {
  text-align: center;
  padding: 16px 0;
  font-size: 13px;
  color: var(--agst-text-color-secondary, #909399);
}

.history-error,
.history-inline-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  color: var(--agst-text-color-secondary, #909399);
  text-align: center;
  overflow-wrap: anywhere;
}

.history-error {
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;

  p { margin: 0; }
}

.history-inline-error { flex-wrap: wrap; }

.history-retry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 32px;
  padding: 4px 12px;
  border: 1px solid var(--agst-color-primary, #2d66f0);
  border-radius: 6px;
  background: transparent;
  color: var(--agst-color-primary, #2d66f0);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--agst-color-primary, #2d66f0);
    outline-offset: 2px;
  }
}

</style>
